import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://adhvaacaddemy-in-1.onrender.com';

const SectionEditModal = ({ section, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageContent, setPageContent] = useState([]);

  // Fetch existing content for this section
  useEffect(() => {
    if (isOpen && section) {
      fetchSectionContent();
    }
  }, [isOpen, section]);

  const fetchSectionContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/api/page-content/home/${section.type}`
      );
      setPageContent(response.data || []);
      
      // Initialize form with existing content
      const initialData = {};
      response.data.forEach(item => {
        initialData[item.type] = item.content;
      });
      setFormData(initialData);
    } catch (err) {
      console.error('Error fetching section content:', err);
      setError('Failed to load section content');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Save each field to page_content table
      for (const [key, value] of Object.entries(formData)) {
        const existingItem = pageContent.find(item => item.type === key);
        
        if (existingItem) {
          // Update existing content
          await axios.put(
            `${API_BASE_URL}/api/admin/page-content/${existingItem.id}`,
            { content: value }
          );
        } else {
          // Create new content
          await axios.post(
            `${API_BASE_URL}/api/admin/page-content`,
            {
              page: 'home',
              section: section.type,
              type: key,
              content: value,
              order_index: Object.keys(formData).indexOf(key) + 1
            }
          );
        }
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving section content:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  // Define fields for each section type
  const getSectionFields = () => {
    switch (section?.type) {
      case 'hero':
        return [
          { key: 'heading', label: 'Main Heading', type: 'text', placeholder: 'Your Future Starts Here' },
          { key: 'subheading', label: 'Subheading', type: 'text', placeholder: 'Master the UPSC with Expert Guidance' },
          { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Brief description...' },
          { key: 'cta_text', label: 'Button Text', type: 'text', placeholder: 'Enroll Now' },
          { key: 'cta_link', label: 'Button Link', type: 'text', placeholder: '/contact' }
        ];
      
      case 'why-choose':
        return [
          { key: 'heading', label: 'Section Heading', type: 'text', placeholder: 'Why Choose Adhva Academy?' },
          { key: 'card1_title', label: 'Card 1 Title', type: 'text', placeholder: 'Expert Mentorship' },
          { key: 'card1_description', label: 'Card 1 Description', type: 'textarea', placeholder: 'Description...' },
          { key: 'card2_title', label: 'Card 2 Title', type: 'text', placeholder: 'Comprehensive Curriculum' },
          { key: 'card2_description', label: 'Card 2 Description', type: 'textarea', placeholder: 'Description...' },
          { key: 'card3_title', label: 'Card 3 Title', type: 'text', placeholder: 'Proven Results' },
          { key: 'card3_description', label: 'Card 3 Description', type: 'textarea', placeholder: 'Description...' },
          { key: 'card4_title', label: 'Card 4 Title', type: 'text', placeholder: 'Community Support' },
          { key: 'card4_description', label: 'Card 4 Description', type: 'textarea', placeholder: 'Description...' }
        ];
      
      case 'ncert-showcase':
        return [
          { key: 'label', label: 'Label Text', type: 'text', placeholder: 'NCERT Mastery' },
          { key: 'heading', label: 'Heading', type: 'text', placeholder: 'Master NCERT for UPSC Success' },
          { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Detailed description...' },
          { key: 'button_text', label: 'Button Text', type: 'text', placeholder: 'Learn More' },
          { key: 'button_link', label: 'Button Link', type: 'text', placeholder: '/ncert-mastery' },
          { key: 'youtube_url', label: 'YouTube Video URL', type: 'text', placeholder: 'https://www.youtube.com/watch?v=...' }
        ];
      
      case 'ncert-poster':
        return [
          { key: 'heading', label: 'Heading', type: 'text', placeholder: 'NCERT Test Series' },
          { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Description...' },
          { key: 'cta_text', label: 'Button Text', type: 'text', placeholder: 'Enroll Now' },
          { key: 'cta_link', label: 'Button Link', type: 'text', placeholder: '/ncert-mastery' }
        ];
      
      case 'mission-vision':
        return [
          { key: 'mission_heading', label: 'Mission Heading', type: 'text', placeholder: 'Our Mission' },
          { key: 'mission_text', label: 'Mission Text', type: 'textarea', placeholder: 'Our mission statement...' },
          { key: 'vision_heading', label: 'Vision Heading', type: 'text', placeholder: 'Our Vision' },
          { key: 'vision_text', label: 'Vision Text', type: 'textarea', placeholder: 'Our vision statement...' }
        ];
      
      default:
        return [
          { key: 'heading', label: 'Section Heading', type: 'text', placeholder: 'Section Title' },
          { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Section description...' }
        ];
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '2rem'
    }} onClick={onClose}>
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '12px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '1.5rem 2rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)',
          color: 'white'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>
              Edit Section Content
            </h2>
            <p style={{ margin: '0.25rem 0 0 0', opacity: 0.9, fontSize: '0.9rem' }}>
              {section?.name}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: '2rem',
          overflowY: 'auto',
          flex: 1
        }}>
          {loading && !pageContent.length ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: '1rem', color: '#666' }}>Loading section content...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {getSectionFields().map(field => (
                  <div key={field.key}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: 600,
                      color: '#374151'
                    }}>
                      {field.label}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={formData[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        rows={4}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '1rem',
                          fontFamily: 'inherit',
                          resize: 'vertical'
                        }}
                      />
                    ) : (
                      <input
                        type={field.type}
                        value={formData[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          fontSize: '1rem'
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {error && (
                <div style={{
                  marginTop: '1.5rem',
                  padding: '1rem',
                  background: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  color: '#dc2626'
                }}>
                  {error}
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1.5rem 2rem',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          background: '#f9fafb'
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '1rem'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              background: loading ? '#9ca3af' : 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Save size={18} />
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionEditModal;
